import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  ClipboardList,
  PackageCheck,
  Key,
  Truck,
  Navigation,
  Loader2,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { decryptData } from "../utils/storageCrypto";
import { theme } from "../assets/theme/theme";

/* -------------------- STATUS -------------------- */
const STATUS = {
  NAVIGATE: "navigate",
  OTP: "otp",
  DONE: "done",
};

/* -------------------- COMPONENT -------------------- */
const ActiveDropoffStep = ({ step, onComplete, activeRide }) => {
  const [status, setStatus] = useState(STATUS.NAVIGATE);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigateUrl = useMemo(() => buildNavigateUrl(step), [step]);

  /* -------------------- SYNC BACKEND STATUS -------------------- */
  useEffect(() => {
    if (!step) return;

    switch (step.status) {
      case "OTP_GENERATED":
        setStatus(STATUS.OTP);
        break;

      case "COMPLETED":
        setStatus(STATUS.DONE);
        onComplete?.();
        break;

      default:
        setStatus(STATUS.NAVIGATE);
    }
  }, [step, onComplete]);

  /* -------------------- API HANDLERS -------------------- */
  const handleReached = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/salesorder/generate-order-delivered-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            shipment_id: activeRide.shipmentId,
            step_number: step.step,
            DG_id: step.dg_id,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to generate delivery OTP");

      setStatus(STATUS.OTP);
    } catch (err) {
      setErrorMessage(err.message || "Failed to generate delivery OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(
        `${URL}customer/salesorder/verify-order-delivered-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            shipment_id: activeRide.shipmentId,
            step_number: step.step,
            otp: Number(otp),
            DG_id: step.dg_id,
          }),
        }
      );

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "OTP verification failed");

      setStatus(STATUS.DONE);
      onComplete?.();
    } catch (err) {
      setErrorMessage(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-white flex flex-col border shadow-sm overflow-hidden flex-1"
      style={{
        borderRadius: `${theme.radius[24]}px`,
        borderColor: theme.colors.grey002,
        maxHeight: "calc(100vh - 175px)",
      }}
    >
      {/* Location Header with Integrated Maps Navigation */}
      <div
        className="p-3.5 border-b"
        style={{
          borderColor: theme.colors.grey005,
          backgroundColor: theme.colors.grey001,
        }}
      >
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{
                backgroundColor: theme.colors.primaryRed,
                color: theme.colors.pureWhite,
              }}
            >
              <Truck className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h4
                className="font-bold text-xs uppercase tracking-wide"
                style={{
                  fontFamily: "DMSans-SemiBold",
                  color: theme.colors.concrete,
                }}
              >
                Drop-off Location
              </h4>
              <p
                className="text-xs font-semibold leading-snug line-clamp-2 mt-0.5"
                style={{
                  fontFamily: "DMSans-Medium",
                  color: theme.colors.bitumen,
                }}
              >
                {step?.address || "Customer delivery location"}
              </p>
            </div>
          </div>

          {/* Integrated Navigation Button */}
          {navigateUrl && status !== STATUS.DONE && (
            <a
              href={navigateUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all active:scale-95 shrink-0 shadow-xs cursor-pointer"
              style={{
                fontFamily: "DMSans-Medium",
                fontSize: "12px",
                borderColor: theme.colors.grey002,
                backgroundColor: theme.colors.pureWhite,
                color: theme.colors.bitumen,
              }}
            >
              <Navigation className="w-3.5 h-3.5 fill-current" style={{ color: theme.colors.brand }} />
              <span className="font-bold">Maps</span>
              <ExternalLink className="w-3 h-3 text-gray-400" />
            </a>
          )}
        </div>
      </div>

      {/* Main Content Body */}
      <div className="flex-1 overflow-y-auto p-3.5 space-y-3">
        {/* Item Manifest */}
        <div
          className="border rounded-xl p-3 space-y-2"
          style={{
            borderColor: theme.colors.grey002,
            backgroundColor: theme.colors.pureWhite,
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 font-bold text-xs">
              <ClipboardList className="w-3.5 h-3.5" style={{ color: theme.colors.charcoal }} />
              <span
                style={{
                  fontFamily: "DMSans-SemiBold",
                  color: theme.colors.bitumen,
                }}
              >
                Items to Deliver
              </span>
            </div>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{
                fontFamily: "DMMono-Medium",
                backgroundColor: theme.colors.offWhite,
                color: theme.colors.charcoal,
              }}
            >
              {(step?.items || []).length} Types
            </span>
          </div>

          <div
            className="border rounded-lg max-h-[190px] overflow-y-auto divide-y"
            style={{
              borderColor: theme.colors.grey005,
              backgroundColor: theme.colors.grey001,
            }}
          >
            {(step?.items || []).map((item, idx) => (
              <div
                key={item.itemId || idx}
                className="flex justify-between items-center px-3 py-2 text-xs"
              >
                <div
                  className="max-w-[70%] font-medium truncate"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.bitumen,
                  }}
                >
                  {item.name}
                </div>
                <div
                  className="font-bold shrink-0 ml-2"
                  style={{
                    fontFamily: "DMMono-Medium",
                    color: theme.colors.charcoal,
                  }}
                >
                  {item.quantity} {item.uom}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OTP Input Section */}
        {status === STATUS.OTP && (
          <div
            className="p-3.5 rounded-xl border space-y-2.5"
            style={{
              borderColor: `${theme.colors.brand}40`,
              backgroundColor: `${theme.colors.brand}05`,
            }}
          >
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4" style={{ color: theme.colors.brand }} />
              <div>
                <h5
                  className="font-bold text-xs"
                  style={{
                    fontFamily: "DMSans-SemiBold",
                    color: theme.colors.bitumen,
                  }}
                >
                  Enter Delivery OTP
                </h5>
                <p
                  className="text-[10px]"
                  style={{
                    fontFamily: "DMSans-Regular",
                    color: theme.colors.concrete,
                  }}
                >
                  Ask customer for the 4-digit completion code
                </p>
              </div>
            </div>

            <input
              maxLength={4}
              value={otp}
              onChange={(e) =>
                /^[0-9]{0,4}$/.test(e.target.value) && setOtp(e.target.value)
              }
              className="border-2 rounded-xl w-full p-2.5 text-center text-xl font-bold tracking-widest outline-none transition-all"
              style={{
                fontFamily: "DMMono-Medium",
                borderColor: otp.length === 4 ? theme.colors.brand : theme.colors.grey002,
                backgroundColor: theme.colors.pureWhite,
                color: theme.colors.bitumen,
              }}
              placeholder="----"
              inputMode="numeric"
              autoFocus
            />
          </div>
        )}

        {/* Error Notification */}
        {errorMessage && (
          <div
            className="p-2.5 rounded-xl border flex items-center gap-2"
            style={{
              backgroundColor: theme.colors.brandTint,
              borderColor: `${theme.colors.error}40`,
              color: theme.colors.error,
            }}
          >
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <p className="text-[11px] font-medium leading-tight">{errorMessage}</p>
          </div>
        )}

        {/* Completed Status */}
        {status === STATUS.DONE && (
          <div
            className="flex items-center gap-2.5 p-3 rounded-xl border font-bold text-xs"
            style={{
              backgroundColor: `${theme.colors.success}10`,
              borderColor: `${theme.colors.success}30`,
              color: theme.colors.success,
            }}
          >
            <PackageCheck className="w-5 h-5" />
            <span>Delivery Completed</span>
          </div>
        )}
      </div>

      {/* Primary Bottom Action CTA */}
      <div
        className="p-3 border-t bg-white"
        style={{ borderColor: theme.colors.grey005 }}
      >
        {status === STATUS.NAVIGATE && (
          <button
            onClick={handleReached}
            disabled={loading}
            className="w-full py-3.5 text-white rounded-xl font-bold transition-all shadow-md active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
            style={{
              fontFamily: "DMSans-Medium",
              backgroundColor: theme.colors.brand,
              boxShadow: `0 4px 12px ${theme.colors.brand}40`,
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Generating OTP...</span>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4" />
                <span>Reached Drop-off Location</span>
              </>
            )}
          </button>
        )}

        {status === STATUS.OTP && (
          <button
            disabled={otp.length !== 4 || loading}
            onClick={handleVerifyOTP}
            className="w-full py-3.5 rounded-xl font-bold transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 disabled:cursor-not-allowed cursor-pointer"
            style={{
              fontFamily: "DMSans-Medium",
              backgroundColor:
                otp.length === 4 && !loading
                  ? theme.colors.brand
                  : theme.colors.grey002,
              color:
                otp.length === 4 && !loading
                  ? theme.colors.pureWhite
                  : theme.colors.concrete,
              boxShadow:
                otp.length === 4 && !loading
                  ? `0 4px 12px ${theme.colors.brand}40`
                  : "none",
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <Key className="w-4 h-4" />
                <span>Verify OTP & Complete</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/* -------------------- MAPS HELPER -------------------- */
const buildNavigateUrl = (step) => {
  const lat = step?.latitude;
  const lng = step?.longitude;
  if (!lat || !lng) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
};

export default ActiveDropoffStep;